'use client';

type ConsentRegion = 'gdpr' | 'ccpa' | 'none';

interface ConsentRecord {
  version: '1.0';
  timestamp: string;
  region: ConsentRegion;
  choices: {
    necessary: true;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
  };
  userAgent: string;
}

const CONSENT_KEY = 'into23_consent_v1';

export function detectRegion(): ConsentRegion {
  if (typeof navigator === 'undefined') return 'gdpr';

  const lang = navigator.language || '';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';

  const euTZPrefixes = ['Europe/', 'Atlantic/Azores', 'Atlantic/Canary', 'Atlantic/Faroe', 'Atlantic/Madeira', 'Arctic/Longyearbyen'];
  const euCountryCodes = ['AT','BE','BG','CY','CZ','DE','DK','EE','ES','FI','FR','GR','HR','HU','IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK','GB','IS','LI','NO','CH'];
  const usTZPrefixes = ['America/'];

  const isEU = euTZPrefixes.some(p => tz.startsWith(p)) || euCountryCodes.some(c => lang.toUpperCase().endsWith('-' + c));
  const isUS = usTZPrefixes.some(p => tz.startsWith(p)) && (lang.startsWith('en-US') || lang.startsWith('en-CA'));

  if (isEU) return 'gdpr';
  if (isUS) return 'ccpa';
  return 'none';
}

export function saveConsent(region: ConsentRegion, choices: ConsentRecord['choices']): void {
  if (typeof window === 'undefined') return;

  const record: ConsentRecord = {
    version: '1.0',
    timestamp: new Date().toISOString(),
    region,
    choices,
    userAgent: navigator.userAgent
  };

  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch (err) {
    console.error('Failed to save consent:', err);
  }
}

export function getConsent(): ConsentRecord | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as ConsentRecord;
  } catch (err) {
    console.error('Failed to retrieve consent:', err);
    return null;
  }
}

export function isConsentGiven(category: keyof ConsentRecord['choices']): boolean {
  const consent = getConsent();
  if (!consent) return false;
  return consent.choices[category] === true;
}

export function clearConsent(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch (err) {
    console.error('Failed to clear consent:', err);
  }
}

export function logConsentAction(action: string, details?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;

  const logEntry = {
    timestamp: new Date().toISOString(),
    action,
    region: detectRegion(),
    details
  };

  try {
    const logs = localStorage.getItem('into23_consent_logs') || '[]';
    const logArray = JSON.parse(logs) as typeof logEntry[];
    logArray.push(logEntry);
    if (logArray.length > 50) logArray.shift();
    localStorage.setItem('into23_consent_logs', JSON.stringify(logArray));
  } catch (err) {
    console.error('Failed to log consent action:', err);
  }
}
